/**
 * /docs URL scheme (pure; no window access). Two halves since 2026-09-28 (Teo: "we need
 * to split docs into si/docs and cz/docs … need the split to be in the url too"):
 *
 *   /si/docs/…   the Slovenian data        /cz/docs/…   the Czech data (ČÚZK, RÚIAN, …)
 *
 * Within a half:
 *   …/docs                     → Pregled
 *   …/docs/viri                → Viri in licence
 *   …/docs/web                 → King.Prostor Web overview (the theme pages as cards)
 *   …/docs/web/<slug>          → one WEB theme page: the theme's entries as sections
 *                                (`#sloj-<key>`); the slug is the theme's folded name
 *   …/docs/cad                 → King.Prostor CAD overview (the folder pages as cards)
 *   …/docs/cad/<slugs…>        → one KingCAD folder page: the folder's layers as
 *                                sections (`#sloj-<xml_id>`); the slugs are the tree path
 *   …/docs/katalog             → the old Web alias → the Web overview (URL replaced)
 *   …/docs/<key>               → an entry — a product only KingCAD serves keeps its own
 *                                page; a WEB product and a KingCAD LAYER are redirected
 *                                by the page to their theme / folder page + anchor
 *
 * The country is a module-level setting made ONCE per page load (`setDocsCountry`, from
 * the URL in DocsPage.initialize): every path builder takes an optional trailing country
 * and defaults to it, so in-page links stay in their half. A route carries no country —
 * the only cross-country link is the header's SI · CZ switch, a plain <a> (full load).
 * A bare `/docs…` reads as si (the server 301s it to `/si/docs…` anyway); `kingcad` was
 * the CAD segment until 2026-09-28 (the server 301s that too).
 *
 * Trailing slashes are accepted on every form. `viri`, `katalog`, `web` and `cad` are
 * reserved segments: no wiki entry may ever use them as its key. Anything deeper than one
 * segment that is not a `web/` or `cad/` page path is handed on as a key and fails
 * `parseWikiKey`, which the page shows as the "ni zapisa" notice.
 */

import type { WikiCountry } from '../../shared/types/layerWiki.js';

export type DocsCountry = WikiCountry;
export const DOCS_COUNTRIES: readonly DocsCountry[] = ['si', 'cz'];
export const DEFAULT_DOCS_COUNTRY: DocsCountry = 'si';
export const RESERVED_SEGMENTS = ['viri', 'katalog', 'web', 'cad'] as const;

let currentCountry: DocsCountry = DEFAULT_DOCS_COUNTRY;

/** The half this page load is — set once by DocsPage from the URL; the path builders default to it. */
export function setDocsCountry(country: DocsCountry): void {
  currentCountry = country;
}

export function getDocsCountry(): DocsCountry {
  return currentCountry;
}

export function isDocsCountry(s: string | null | undefined): s is DocsCountry {
  return s === 'si' || s === 'cz';
}

/** `/si/docs` / `/cz/docs`. */
export function docsBase(country: DocsCountry = currentCountry): string {
  return `/${country}/docs`;
}

export type DocsRoute =
  | { view: 'pregled' }
  | { view: 'viri' }
  | { view: 'web' }
  | { view: 'webpage'; slug: string }
  | { view: 'cad' }
  | { view: 'cadfolder'; path: string[] }
  | { view: 'katalog'; key: string | null };

export interface DocsUrl {
  country: DocsCountry;
  route: DocsRoute;
}

function decode(seg: string): string {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg; // kept raw — it will fail the key check
  }
}

/** `/si/docs`, `/cz/docs` or the bare legacy `/docs`, followed by `/` or the end. */
const DOCS_URL = /^\/(?:(si|cz)\/)?docs(?=\/|$)/;

/** Country + route for a pathname. Paths outside the docs read as si Pregled. */
export function parseDocsUrl(pathname: string): DocsUrl {
  const m = DOCS_URL.exec(pathname);
  if (!m) return { country: DEFAULT_DOCS_COUNTRY, route: { view: 'pregled' } };
  const country: DocsCountry = m[1] === 'cz' ? 'cz' : 'si';
  return { country, route: parseRest(pathname.slice(m[0].length)) };
}

/** The route half of `parseDocsUrl`. */
export function parseDocsPath(pathname: string): DocsRoute {
  return parseDocsUrl(pathname).route;
}

function parseRest(tail: string): DocsRoute {
  const rest = tail.replace(/^\/+/, '').replace(/\/+$/, '');
  if (rest === '') return { view: 'pregled' };
  const segments = rest.split('/').map(decode);
  const first = segments[0] as string;
  if (segments.length > 1) {
    // Only the KingCAD folder pages and the web theme pages go deeper; anything else is an (invalid) key.
    if (first === 'cad') return { view: 'cadfolder', path: segments.slice(1) };
    if (first === 'web' && segments.length === 2)
      return { view: 'webpage', slug: segments[1] as string };
    return { view: 'katalog', key: segments.join('/') };
  }
  if (first === 'viri') return { view: 'viri' };
  if (first === 'web') return { view: 'web' };
  if (first === 'cad') return { view: 'cad' };
  if (first === 'katalog') return { view: 'katalog', key: null };
  return { view: 'katalog', key: first };
}

/** Pathname for a route in a half (the inverse of `parseDocsUrl`). */
export function docsPath(route: DocsRoute, country: DocsCountry = currentCountry): string {
  const base = docsBase(country);
  switch (route.view) {
    case 'pregled':
      return base;
    case 'viri':
      return `${base}/viri`;
    case 'web':
      return `${base}/web`;
    case 'webpage':
      return `${base}/web/${encodeURIComponent(route.slug)}`;
    case 'cad':
      return `${base}/cad`;
    case 'cadfolder':
      return `${base}/cad/${route.path.map(encodeURIComponent).join('/')}`;
    case 'katalog':
      return route.key === null ? `${base}/katalog` : `${base}/${encodeURIComponent(route.key)}`;
  }
}

/** `…/docs/<key>` — the entry link every list on the page uses. */
export function entryPath(key: string, country: DocsCountry = currentCountry): string {
  return docsPath({ view: 'katalog', key }, country);
}

/** `…/docs/web/<slug>` — a web theme page. */
export function webPagePath(slug: string, country: DocsCountry = currentCountry): string {
  return docsPath({ view: 'webpage', slug }, country);
}

/** `…/docs/cad/<slugs…>` — a KingCAD folder page. */
export function cadFolderPath(slugs: readonly string[], country: DocsCountry = currentCountry): string {
  return docsPath({ view: 'cadfolder', path: [...slugs] }, country);
}

/** Anchor id of a publisher's section on the Viri in licence view. */
export function sourceAnchorId(sourceId: string): string {
  return `vir-${sourceId}`;
}

/** `…/docs/viri#vir-<sourceId>` — where an entry's »Vir« row points. */
export function viriSourcePath(sourceId: string, country: DocsCountry = currentCountry): string {
  return `${docsPath({ view: 'viri' }, country)}#${sourceAnchorId(sourceId)}`;
}

/** Anchor id of one layer's section on its folder page (`sloj-<xml_id>`). */
export function cadLayerAnchorId(xmlId: string): string {
  return `sloj-${xmlId}`;
}

/** Anchor id of one entry's section on its theme page (`sloj-<key>`). */
export function webEntryAnchorId(key: string): string {
  return `sloj-${key}`;
}

export function isSameRoute(a: DocsRoute, b: DocsRoute): boolean {
  if (a.view !== b.view) return false;
  if (a.view === 'katalog' && b.view === 'katalog') return a.key === b.key;
  if (a.view === 'webpage' && b.view === 'webpage') return a.slug === b.slug;
  if (a.view === 'cadfolder' && b.view === 'cadfolder')
    return a.path.join('/') === b.path.join('/');
  return true;
}
